// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the engine's schema, and what a flush becomes
//  ---------------------------------------------------------------------------
//  Every SQL string the app executes against its own database is written here,
//  and nothing here executes anything. That split is deliberate and it is the
//  same one the loop's release gate makes: the decisions live in pure
//  functions over plain data, so they can be proven without a database, a
//  worker, a browser or a deploy — and then the worker and the tests run the
//  identical strings.
//
//  THE TABLE IS THE SERVER'S TABLE. migrations/0001_init.sql declares
//  docs(user_id, key, value, client_seen_at, rev, deleted) with PRIMARY KEY
//  (user_id, key). Locally there is one account, so user_id goes; `rev` and
//  `deleted` belong to the sync protocol, not to storage, and md-sync.js
//  already keeps them elsewhere. What is left is key, value, and when it last
//  moved. Storage unit = sync unit = row, which is the invariant the whole app
//  already rests on, and the reason a document can be pushed without being
//  reshaped first.
//
//  `updated_at` is the local clock at the moment of the flush, and it is not a
//  revision. It answers one question — "is anything newer than what I last
//  read?" — which is the question md-record-index.ts:91 currently answers by
//  hashing every byte of every document on the device. That it is indexed is
//  part of this schema rather than a later addition, because by the time C2
//  wants it the file holds somebody's whole account.
//
//  There is no WITHOUT ROWID here, on purpose. It would be marginally smaller
//  and it would foreclose FTS5 external-content in C3, which joins on rowid.
// ════════════════════════════════════════════════════════════════════════════
(function (g: any) {
  'use strict';

  /* Bumped when the shape below changes in a way an existing file cannot be
     read with. It is recorded IN the migration marker, so a copy made by an
     older schema is redone rather than trusted.

     2 — the `meta` table (B5). Additive, and IF NOT EXISTS like the rest, so a
         version 1 file becomes a version 2 file by being opened.
     3 — `search` and `search_fts` (C3). Also additive, and also arrived at by
         being opened — but an existing file's `docs` were written before the
         index existed, so opening is not enough on its own: md-engine-ops.js
         backfills once and records that it did. */
  const SCHEMA_VERSION = 3;

  const SCHEMA: string[] = [
    `CREATE TABLE IF NOT EXISTS docs (
       key        TEXT    PRIMARY KEY,
       value      TEXT    NOT NULL,
       updated_at INTEGER NOT NULL
     )`,
    `CREATE INDEX IF NOT EXISTS docs_updated_at ON docs(updated_at)`,
    /* Everything the ENGINE knows about itself, which is nothing the user
       wrote. It is a separate table rather than a reserved key range in `docs`
       because load() hands docs straight to the app: a marker in there would be
       hydrated as though somebody had written it. */
    `CREATE TABLE IF NOT EXISTS meta (
       key   TEXT PRIMARY KEY,
       value TEXT NOT NULL
     )`,
    /* ── search ────────────────────────────────────────────────────────────
       `search` holds the HARVESTED words of a document — see md-search.ts —
       and not its JSON. External content straight over `docs` would index
       every brace, every id, and the word "title" in every document on the
       account; over a board scene or a database row the harvest is a small
       fraction of the bytes. One copy of the words, and snippet() has
       something to quote from.

       `key` is the primary key so a flush can upsert and delete by the same
       name it already has. The rowid underneath is what search_fts joins on,
       and SQLite keeps it stable across an upsert — which is precisely why
       docs has no WITHOUT ROWID either. */
    `CREATE TABLE IF NOT EXISTS search (
       key   TEXT PRIMARY KEY,
       kind  TEXT NOT NULL,
       title TEXT NOT NULL,
       text  TEXT NOT NULL
     )`,
    `CREATE INDEX IF NOT EXISTS search_kind ON search(kind)`,
    /* remove_diacritics 2 is the Unicode-correct one; the app is written in
       English and Arabic and a search for "Quran" must find "Qur'ān". */
    `CREATE VIRTUAL TABLE IF NOT EXISTS search_fts USING fts5(
       title, text,
       content='search', content_rowid='rowid',
       tokenize='unicode61 remove_diacritics 2'
     )`,
    /* The documented way to keep an external-content index honest. In triggers
       rather than in the flush loop because a row can also move by a path this
       app has not written yet — a repair, an import, a later migration — and
       an index that is only correct when one particular function was the one
       that wrote is an index that is quietly wrong. */
    `CREATE TRIGGER IF NOT EXISTS search_ai AFTER INSERT ON search BEGIN
       INSERT INTO search_fts(rowid, title, text) VALUES (new.rowid, new.title, new.text);
     END`,
    `CREATE TRIGGER IF NOT EXISTS search_ad AFTER DELETE ON search BEGIN
       INSERT INTO search_fts(search_fts, rowid, title, text) VALUES ('delete', old.rowid, old.title, old.text);
     END`,
    `CREATE TRIGGER IF NOT EXISTS search_au AFTER UPDATE ON search BEGIN
       INSERT INTO search_fts(search_fts, rowid, title, text) VALUES ('delete', old.rowid, old.title, old.text);
       INSERT INTO search_fts(rowid, title, text) VALUES (new.rowid, new.title, new.text);
     END`,
  ];

  /* Ordered by key so a load is deterministic. It costs nothing — key is the
     primary key, so the index is already in that order — and it means two
     boots of the same file produce the same array, which is what lets B5
     compare a migration byte for byte. */
  const LOAD_SQL = 'SELECT key, value FROM docs ORDER BY key';

  const PUT_SQL =
    `INSERT INTO docs (key, value, updated_at) VALUES (?, ?, ?)
     ON CONFLICT(key) DO UPDATE SET value = excluded.value, updated_at = excluded.updated_at`;

  const DELETE_SQL = 'DELETE FROM docs WHERE key = ?';

  const SEARCH_PUT_SQL =
    `INSERT INTO search (key, kind, title, text) VALUES (?, ?, ?, ?)
     ON CONFLICT(key) DO UPDATE SET kind = excluded.kind, title = excluded.title, text = excluded.text`;

  const SEARCH_DELETE_SQL = 'DELETE FROM search WHERE key = ?';

  /* ── the marks ─────────────────────────────────────────────────────────────
     snippet() wraps every matched word in this pair so the palette can light
     the words that earned the result. Private-use characters, because no
     keyboard on earth types them — they can never collide with somebody's own
     writing, and mdFindMarks (md-find.ts) strips them even when a snippet is
     cut through the middle of a pair. */
  const MD_SEARCH_MARK = ['\uE000', '\uE001'];

  /* ── the query ─────────────────────────────────────────────────────────────
     bm25()'s weights are per column and higher means more: a hit in the title
     is worth ten of a hit in the body. It returns a NEGATIVE score where a
     better match is more negative, so ORDER BY rank ascending is best-first —
     which reads backwards and is why it is written down here once.

     snippet() quotes from the text column with the match marked, so a result
     can be drawn without reading the document it came from. That is the whole
     reason `search` keeps the words rather than pointing at `docs`. */
  const SEARCH_QUERY_SQL =
    `SELECT s.key AS key, s.kind AS kind, s.title AS title,
            snippet(search_fts, 1, '\uE000', '\uE001', '…', 12) AS snippet,
            bm25(search_fts, 10.0, 1.0) AS rank
       FROM search_fts JOIN search s ON s.rowid = search_fts.rowid
      WHERE search_fts MATCH ?
      ORDER BY rank
      LIMIT ?`;

  const SEARCH_SCOPED_SQL =
    `SELECT s.key AS key, s.kind AS kind, s.title AS title,
            snippet(search_fts, 1, '\uE000', '\uE001', '…', 12) AS snippet,
            bm25(search_fts, 10.0, 1.0) AS rank
       FROM search_fts JOIN search s ON s.rowid = search_fts.rowid
      WHERE search_fts MATCH ? AND s.kind = ?
      ORDER BY rank
      LIMIT ?`;

  /* What a backfill reads. Not LOAD_SQL: that one is ordered by key so a
     migration can be compared byte for byte, and this one has no such promise
     to keep and no reason to pay for it. */
  const SEARCH_SCAN_SQL = 'SELECT key, value FROM docs';

  const META_GET_SQL = 'SELECT value FROM meta WHERE key = ?';
  const META_SET_SQL =
    `INSERT INTO meta (key, value) VALUES (?, ?)
     ON CONFLICT(key) DO UPDATE SET value = excluded.value`;

  type Step = { sql?: string; bind?: any[]; key?: any; refused?: string };

  /* ── a flush, as data ─────────────────────────────────────────────────────
     md-storage.js's `dirty` map is exactly `key -> raw string, or null for a
     removal`, so that is what this takes — as a Map, or as the pairs a Map
     iterates into, so a test can write a literal.

     IT NEVER THROWS. The module above it promises a write never throws and
     always reports, and a plan that throws halfway through would break both:
     the writes before the bad one would already be in the transaction, and the
     caller would get an exception instead of an answer. So a malformed entry
     becomes a step with `refused` and no SQL — the worker skips it and reports
     it as a fault through the path that already reaches the banner, and every
     well-formed write around it still lands. */
  function plan(writes: any, now: number): Step[] {
    const entries = writes instanceof Map ? [...writes.entries()] : Array.isArray(writes) ? writes : [];
    const out: Step[] = [];
    const timed = typeof now === 'number' && Number.isFinite(now);
    for (const entry of entries) {
      const key = entry && entry[0];
      const value = entry && entry[1];
      if (typeof key !== 'string' || !key) {
        out.push({ key, refused: 'key must be a non-empty string' });
        continue;
      }
      if (value === null || value === undefined) {
        out.push({ sql: DELETE_SQL, bind: [key], key });
        continue;
      }
      if (typeof value !== 'string') {
        out.push({ key, refused: 'value must be a string or null' });
        continue;
      }
      if (!timed) {
        out.push({ key, refused: 'time must be a finite number' });
        continue;
      }
      out.push({ sql: PUT_SQL, bind: [key, value, now], key });
    }
    return out;
  }

  Object.assign(g, {
    MD_ENGINE_SCHEMA_VERSION: SCHEMA_VERSION,
    MD_ENGINE_LOAD_SQL: LOAD_SQL,
    MD_ENGINE_PUT_SQL: PUT_SQL,
    MD_ENGINE_DELETE_SQL: DELETE_SQL,
    MD_ENGINE_META_GET_SQL: META_GET_SQL,
    MD_SEARCH_PUT_SQL: SEARCH_PUT_SQL,
    MD_SEARCH_DELETE_SQL: SEARCH_DELETE_SQL,
    MD_SEARCH_QUERY_SQL: SEARCH_QUERY_SQL,
    MD_SEARCH_SCOPED_SQL: SEARCH_SCOPED_SQL,
    MD_SEARCH_SCAN_SQL: SEARCH_SCAN_SQL,
    MD_SEARCH_MARKER: 'search:built',
    MD_SEARCH_MARK,
    MD_ENGINE_META_SET_SQL: META_SET_SQL,
    mdEngineSchema: () => SCHEMA.slice(),
    mdEnginePlan: plan,
  });
})(typeof window !== 'undefined' ? window : globalThis);
